import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { TunerService } from 'src/app/service/tuner.service';
import { DowngradePreviewResponse, DowngradeConfirmResponse } from 'src/app/model/response/downgrade-preview-response';

/**
 * Downgrade de tuner com afiliados: dois passos, nunca um só.
 *
 * Passo 1 (`select`) — com `requiresSelection`, o tuner escolhe exatamente
 * `mustKeep` clientes; sem ela (ex.: Free, teto zero), só mostra o cashback
 * mensal que ele deixa de receber, sem lista.
 *
 * Passo 2 (`sure`) — a confirmação separada. Ela já dispara o `confirm`, porque
 * é ele (não o preview) quem devolve a data da virada: sem chamar aqui, o aviso
 * "isso acontece na virada, não agora" não teria data nenhuma pra mostrar. Só
 * depois de ver o resultado é que o tuner clica em continuar — e é só então que
 * o `confirmed` sai, liberando o checkout que já existia.
 */
@Component({
  selector: 'app-downgrade-affiliates-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, ProgressSpinnerModule, TranslocoRootModule],
  templateUrl: './downgrade-affiliates-modal.component.html',
  styleUrl: './downgrade-affiliates-modal.component.css',
})
export class DowngradeAffiliatesModalComponent implements OnChanges {
  @Input() tunerId!: string;
  @Input() targetPlanId!: string;
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  preview: DowngradePreviewResponse | null = null;
  loadingPreview = false;
  previewError = false;

  selected = new Set<string>();
  step: 'select' | 'sure' = 'select';

  confirming = false;
  confirmError = false;
  /**
   * `true` para os códigos `tuner.downgrade.*` de validação (ex.: `no_active_period`,
   * `not_a_downgrade`): condição do tuner que não muda ao tentar de novo, então não faz
   * sentido oferecer retry — só uma saída. `false` (ou erro sem `code`, ex. rede) mantém o
   * retry, que é o caso em que tentar de novo pode dar certo.
   */
  confirmErrorPermanent = false;
  confirmResult: DowngradeConfirmResponse | null = null;

  constructor(private tunerService: TunerService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['tunerId'] || changes['targetPlanId']) && this.tunerId && this.targetPlanId) {
      this.loadPreview();
    }
  }

  retryLoadPreview(): void {
    this.loadPreview();
  }

  private loadPreview(): void {
    this.loadingPreview = true;
    this.previewError = false;
    this.step = 'select';
    this.selected = new Set();
    this.confirmResult = null;
    this.tunerService.getDowngradePreview(this.tunerId, this.targetPlanId).subscribe({
      next: (preview) => {
        this.preview = preview;
        this.loadingPreview = false;
      },
      error: () => {
        this.loadingPreview = false;
        this.previewError = true;
      },
    });
  }

  isSelected(userId: string): boolean {
    return this.selected.has(userId);
  }

  toggle(userId: string): void {
    if (this.selected.has(userId)) {
      this.selected.delete(userId);
      return;
    }
    // Trava em mustKeep: não dá pra marcar um a mais do que o teto permite.
    if (this.preview?.mustKeep != null && this.selected.size >= this.preview.mustKeep) return;
    this.selected.add(userId);
  }

  /** Perda derivada do total menos os mantidos: um número só, sem chance de divergir. */
  get cashbackLoss(): number {
    if (!this.preview) return 0;
    const kept = this.preview.clients
      .filter((c) => this.selected.has(c.userId))
      .reduce((sum, c) => sum + c.monthlyCashback, 0);
    return this.preview.monthlyCashbackTotal - kept;
  }

  /**
   * Estimativa local, só para o passo 1 (enquanto ele escolhe, não há resposta do servidor
   * ainda). O passo 2 NÃO usa este getter — usa `confirmResult.willRemoveCount`, a leitura
   * fresca do banco que o `confirm` devolve, porque entre o preview e o confirm outro cliente
   * pode ter sido aceito.
   */
  get removedCount(): number {
    if (!this.preview) return 0;
    return this.preview.requiresSelection
      ? this.preview.activeCount - this.selected.size
      : 0;
  }

  /** Símbolo/código a exibir antes do valor — mesmo padrão de tuner-dashboard (wallet.currency). */
  get currencyPrefix(): string {
    const currency = this.preview?.currency;
    return !currency || currency === 'BRL' ? 'R$' : currency;
  }

  get canAdvance(): boolean {
    if (!this.preview) return false;
    return !this.preview.requiresSelection
        || this.selected.size === this.preview.mustKeep;
  }

  advance(): void {
    if (!this.canAdvance || !this.preview) return;
    this.step = 'sure';
    this.confirming = true;
    this.confirmError = false;
    this.confirmErrorPermanent = false;
    const keepUserIds = this.preview.requiresSelection ? Array.from(this.selected) : [];
    this.tunerService.confirmDowngrade(this.tunerId, this.targetPlanId, keepUserIds).subscribe({
      next: (result) => {
        this.confirming = false;
        this.confirmResult = result;
      },
      error: (err: HttpErrorResponse) => {
        this.confirming = false;
        this.confirmError = true;
        // tuner.downgrade.* é sempre validação de negócio (ex.: no_active_period — cobrança
        // manual sem assinatura Stripe; not_a_downgrade) — a condição não muda entre uma
        // tentativa e a próxima, então repetir só falharia de novo. Qualquer outro erro (rede,
        // 500) é transitório e o retry continua fazendo sentido.
        const code = err?.error?.code;
        this.confirmErrorPermanent = typeof code === 'string' && code.startsWith('tuner.downgrade.');
      },
    });
  }

  retryConfirm(): void {
    this.advance();
  }

  /** Já chamou o confirm ao entrar no passo 2 — aqui só libera o checkout que já existia. */
  proceed(): void {
    this.confirmed.emit();
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
